"use client";

// Shared plumbing for the fixed-size animated bento stages (560x230): fit the
// stage to its container, and run a looping clock only while it is on screen.

import * as React from "react";

/** Scale (max 1) that fits a stage of `stageWidth` px into the element's width. */
export function useStageScale(
  rootRef: React.RefObject<HTMLElement | null>,
  stageWidth: number
) {
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, el.clientWidth / stageWidth));
    fit();
    const resize = new ResizeObserver(fit);
    resize.observe(el);
    return () => resize.disconnect();
  }, [rootRef, stageWidth]);

  return scale;
}

/**
 * Elapsed seconds of a rAF loop that runs only while `rootRef` is on screen and
 * restarts from 0 each time it re-enters. Reduced-motion visitors stay on
 * `restSeconds`. `initialSeconds` is what shows before the loop starts
 * (default: `restSeconds`). The caller wraps the value with its own loop length.
 */
export function useLoopSeconds(
  rootRef: React.RefObject<HTMLElement | null>,
  restSeconds: number,
  initialSeconds = restSeconds
) {
  const [seconds, setSeconds] = React.useState(initialSeconds);

  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setSeconds(restSeconds);
      return;
    }

    let frameId = 0;
    let started = 0;
    const tick = (now: number) => {
      if (!started) started = now;
      setSeconds((now - started) / 1000);
      frameId = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        started = 0;
        frameId = requestAnimationFrame(tick);
      } else {
        cancelAnimationFrame(frameId);
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, [rootRef, restSeconds]);

  return seconds;
}
